// ROOM 404: Explicit Push Notification Consent & Preference Modal
// Pre-prompt education, iOS PWA requirements, state handling (denied, dismissed, unsupported, revoked),
// and opt-in mission toggles.

import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  BellOff,
  CheckCircle,
  HelpCircle,
  Megaphone,
  Shield,
  Smartphone,
  Sparkles,
  Target,
  X,
} from 'lucide-react';
import {
  getActivePushSubscription,
  getDeviceAndPushSupport,
  requestAndSubscribePush,
  unsubscribePush,
} from '../services/push';

interface PushConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenInstallGuide: () => void;
}

export const PushConsentModal: React.FC<PushConsentModalProps> = ({
  isOpen,
  onClose,
  onOpenInstallGuide,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [optInMissions, setOptInMissions] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [status, setStatus] = useState(getDeviceAndPushSupport());

  useEffect(() => {
    if (!isOpen) return;
    setStatus(getDeviceAndPushSupport());
    setError(null);
    setSuccessMsg(null);

    getActivePushSubscription().then((sub) => {
      setIsSubscribed(!!sub);
    });
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEnablePush = async () => {
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    // If iOS and not standalone, guide to install first
    if (status.isIos && !status.isStandalone) {
      setLoading(false);
      onClose();
      onOpenInstallGuide();
      return;
    }

    const res = await requestAndSubscribePush({ optInMissions });
    setLoading(false);

    if (res.success) {
      setIsSubscribed(true);
      setStatus(getDeviceAndPushSupport());
      setSuccessMsg('High-priority push notifications activated on this device!');
      setTimeout(() => {
        onClose();
      }, 2000);
    } else {
      setError(res.error || 'Failed to activate notifications.');
      setStatus(getDeviceAndPushSupport());
    }
  };

  const handleDisablePush = async () => {
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    const res = await unsubscribePush();
    setLoading(false);

    if (res.success) {
      setIsSubscribed(false);
      setSuccessMsg('Push notifications have been deactivated on this device.');
    } else {
      setError(res.error || 'Failed to unsubscribe.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="w-full max-w-md bg-[#12141c] border border-[#1f2433] rounded-3xl p-6 shadow-2xl space-y-4 text-xs text-gray-200 relative">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#1f2433] pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <Bell className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">Emergency & Priority Push</h3>
              <p className="text-[11px] text-gray-400 font-mono">Guaranteed zero-spam society alerts</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-[#1f2433] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Banners */}
        {error && (
          <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs">
            {error}
          </div>
        )}

        {successMsg && (
          <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono">
            {successMsg}
          </div>
        )}

        {/* State: iOS Non-Standalone PWA Notice */}
        {status.isIos && !status.isStandalone && (
          <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-300 space-y-2">
            <div className="font-bold flex items-center space-x-1.5 text-xs text-amber-200">
              <Smartphone className="w-4 h-4 text-amber-400" />
              <span>iOS Installation Required First</span>
            </div>
            <p className="text-[11px] text-amber-400/90 leading-relaxed">
              On iPhone & iPad, Apple restricts Web Push to web apps added to the Home Screen. Add ROOM 404 to your Home Screen first to enable alerts.
            </p>
            <button
              onClick={() => {
                onClose();
                onOpenInstallGuide();
              }}
              className="text-xs text-amber-200 underline hover:text-white font-semibold"
            >
              View step-by-step iOS install guide →
            </button>
          </div>
        )}

        {/* State: Denied Permission Warning */}
        {status.permission === 'denied' && (
          <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-300 space-y-1.5">
            <div className="font-bold text-xs flex items-center space-x-1.5 text-rose-200">
              <BellOff className="w-4 h-4 text-rose-400" />
              <span>Notifications Blocked by Browser</span>
            </div>
            <p className="text-[11px] text-rose-400/90 leading-relaxed">
              You previously blocked notifications for ROOM 404. To re-enable them, open your browser site settings and allow notifications.
            </p>
          </div>
        )}

        {/* Pre-prompt Value Proposition */}
        <div className="space-y-3">
          <div className="p-3.5 rounded-2xl bg-[#090a0f] border border-[#1f2433] space-y-2.5">
            <div className="font-bold text-white text-xs">Why enable Society Push?</div>
            <ul className="space-y-2 text-[11px] text-gray-300">
              <li className="flex items-start space-x-2">
                <Megaphone className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Emergency Bulletins:</strong> Urgent society announcements and safety notices from Administrators.
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Target className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Mission Deadlines:</strong> Reminders when your accepted society missions approach deadlines.
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Strict Privacy Guard:</strong> Payloads never carry real legal identities, enrollment data, or chat contents.
                </span>
              </li>
            </ul>
          </div>

          {/* Preferences Toggle */}
          <div className="p-3 rounded-2xl bg-[#090a0f] border border-[#1f2433] flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="text-white font-semibold text-xs">Include Mission Deadlines</div>
              <div className="text-[11px] text-gray-400">Receive alerts 24h before mission expiry</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={optInMissions}
                onChange={(e) => setOptInMissions(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-[#1f2433] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-2 border-t border-[#1f2433] flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Not Now
          </button>

          {isSubscribed ? (
            <button
              onClick={handleDisablePush}
              disabled={loading}
              className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 font-semibold rounded-xl text-xs transition-colors disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Disable Notifications'}
            </button>
          ) : (
            <button
              onClick={handleEnablePush}
              disabled={loading || status.permission === 'denied'}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-xl text-xs flex items-center space-x-2 shadow-lg shadow-emerald-500/10 active:scale-95 transition-all disabled:opacity-50"
            >
              <span>{loading ? 'Registering...' : 'Enable Notifications'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
